<template>
    <VueFinalModal
        v-if="!showFeedbackModalThanks"
        v-model="showModal"
        class="SendJobRequestModal flex justify-center items-start sm:items-center p-0 sm:p-4"
        content-class="flex flex-col w-full max-w-3xl max-h-[100vh] sm:max-h-[92vh] h-full sm:h-auto bg-white dark:bg-surface-card sm:border sm:border-gray-200 dark:sm:border-default sm:rounded-2xl shadow-2xl overflow-hidden"
        :click-to-close="!(step > 1 && state !== 'done')"
        :esc-to-close="!(step > 1 && state !== 'done')"
        content-transition="vfm-fade"
        overlay-transition="vfm-fade"
        @update:model-value="handleModalUpdate"
    >
        <div v-if="state === 'loading'" class="absolute inset-0 bg-white/75 dark:bg-surface-card/75 flex items-center justify-center z-10">
            <div class="animate-spin rounded-full size-12 border-b-2 border-primary"></div>
        </div>

        <!-- Modal Header -->
        <div class="shrink-0 border-b border-gray-100 dark:border-default px-5 sm:px-7 pt-5 pb-4">
            <p class="text-xs font-medium uppercase tracking-wide text-primary mb-2">
                {{ $t(headline.title, lang) }}
            </p>
            <div v-if="job.title && state !== 'done'" class="flex items-start gap-3">
                <div v-if="org?.image || job.orgMeta?.image" class="shrink-0">
                    <img
                        :alt="org?.title || job.orgMeta?.title || ''"
                        class="size-12 rounded-lg object-cover border border-gray-100 dark:border-default bg-white dark:bg-surface-card"
                        :src="org?.image || job.orgMeta?.image"
                    />
                </div>
                <div class="min-w-0 flex-1">
                    <h2 class="text-lg sm:text-xl font-semibold leading-tight text-gray-900 dark:text-txt-body line-clamp-2">
                        {{ job.title }}
                    </h2>
                    <p class="text-sm text-gray-500 dark:text-txt-body mt-0.5 truncate">
                        {{ org?.title || job.orgMeta?.title }}
                    </p>
                    <div v-if="jobPlaceText || jobTimeText || jobTypeText || salaryText" class="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-xs text-gray-600 dark:text-txt-body">
                        <span v-if="jobPlaceText" class="flex items-center gap-1">
                            <CIcon name="location-outline" class="size-3.5" />
                            {{ jobPlaceText }}
                        </span>
                        <span v-if="jobTimeText" class="flex items-center gap-1">
                            <CIcon name="clock" class="size-3.5" />
                            {{ jobTimeText }}
                        </span>
                        <span v-if="jobTypeText" class="flex items-center gap-1">
                            <CIcon name="jobs" class="size-3.5" />
                            {{ jobTypeText }}
                        </span>
                        <span v-if="salaryText" class="flex items-center gap-1">
                            <span class="font-medium">€</span>
                            {{ salaryText }}
                        </span>
                    </div>
                </div>
            </div>
        </div>

        <!-- Scrollable Content Area -->
        <div class="flex-1 overflow-y-auto px-5 sm:px-7 py-5">
        <template v-if="state !== 'done'">
            <div v-if="state === 'error'" class="mb-5 p-3 bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900/50 rounded-lg flex items-start gap-2">
                <svg class="size-5 text-red-500 shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                </svg>
                <div class="text-sm text-red-700 dark:text-red-300">
                    {{ errors.general || 'An unknown error occurred' }}
                </div>
            </div>

            <div v-if="step === 1" class="space-y-5">
                <!-- Personal info section -->
                <div class="space-y-3">
                    <div v-if="!userProfile" class="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-3">
                        <FirstName v-model="unregisteredUser.firstName" :error="errors.firstName" :lang="lang" required />
                        <LastName v-model="unregisteredUser.lastName" :error="errors.lastName" :lang="lang" required />
                    </div>

                    <div class="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-3">
                        <CFormGroup :error="errors.email" :label="$t(`${keyPrefix}.fields.email`, lang)" required>
                            <CFormInput v-model="jobRequest.email" autocomplete="email" />
                        </CFormGroup>

                        <CFormGroup :error="errors.phone" :label="$t(`${keyPrefix}.fields.phone`, lang)" required>
                            <CFormInput v-model="jobRequest.phone" autocomplete="tel" />
                        </CFormGroup>
                    </div>
                </div>

                <!-- About section -->
                <div class="space-y-3">
                    <CFormGroup
                        :error="errors.motivation"
                        :label="$t(`${keyPrefix}.fields.motivation`, lang)"
                        :label-info="`${getLength(jobRequest.motivation)}/700`"
                        required
                    >
                        <CFormTextarea v-model="jobRequest.motivation" maxlength="700" />
                    </CFormGroup>

                    <CFormGroup
                        :label="$t(`${keyPrefix}.fields.about`, lang)"
                        :label-info="`${getLength(jobRequest.about)}/700`"
                    >
                        <CFormTextarea v-model="jobRequest.about" maxlength="700" />
                    </CFormGroup>

                    <CFormGroup :label="$t(`${keyPrefix}.fields.linkedIn`, lang)">
                        <CFormInput
                            v-model="jobRequest.linkedInUrl"
                            placeholder="https://www.linkedin.com/in/meno-priezvisko/"
                        />
                    </CFormGroup>
                </div>

                <!-- Documents section -->
                <div class="rounded-xl border border-gray-200 dark:border-default bg-gray-50/50 dark:bg-surface-card/30 p-4 space-y-4">
                    <h3 class="text-sm font-semibold text-gray-900 dark:text-txt-body">
                        {{ $t(`${keyPrefix}.fields.documentsTitle`, lang) }}
                    </h3>
                    <CFormGroup :error="errors.cvFile" :label="$t(`${keyPrefix}.fields.cvFile.title`, lang)" required>
                        <SavedJobCvInput v-model="cvFile" :lang="lang" :disabled="state === 'loading'" />
                    </CFormGroup>

                    <div>
                        <CLabel class="mb-2 w-auto">
                            {{ $t(`${keyPrefix}.fields.clFile.title`, lang) }}
                        </CLabel>

                        <div v-for="(file, index) in clFiles" :key="index" class="input relative mb-2 flex items-center">
                            <div class="flex max-w-full flex-auto items-center">
                                <div class="truncate font-semibold">
                                    {{ file.file.name }}
                                </div>
                                <div class="ml-2 flex-none text-sm italic text-opacity-25">({{ formatFileSize(file.file.size) }})</div>
                                <div class="-mr-1 ml-auto shrink-0 pl-2">
                                    <CButton class="!ml-0 max-h-8 flex-none !px-2" variant="text" @click="clearFile(index)">
                                        {{ $t('ui.CFormFileInput.remove', lang) }}
                                    </CButton>
                                </div>
                            </div>
                        </div>

                        <CButton
                            v-if="clFiles.length < 5"
                            class="w-full sm:w-auto"
                            :disabled="state === 'loading'"
                            icon="add"
                            variant="primary-link"
                            @click="openFilePicker()"
                        >
                            {{ $t(`${keyPrefix}.fields.clFile.label`, lang) }}
                        </CButton>

                        <input
                            ref="clFileInput"
                            :accept="`${PDF_MIME_TYPE},${DOCX_MIME_TYPE},.docx`"
                            class="hidden"
                            type="file"
                            @change="handleFileChange"
                        />

                        <div v-if="errors.clFile" class="mt-1 text-sm text-red-500">
                            {{ errors.clFile }}
                        </div>
                    </div>
                </div>

                <CCardBox v-if="isPublicSectorWithRedirection" variant="flat">
                    <span v-html="$t(`${keyPrefix}.fields.pubSecLink`, lang)"></span>
                </CCardBox>

                <!-- Self-assessment (sebapoznanie) sharing — logged-in candidates only -->
                <div v-if="userProfile" class="rounded-xl border border-primary/30 bg-primary/5 p-4 space-y-3">
                    <div class="flex items-start gap-2">
                        <span class="text-xl leading-none">🧭</span>
                        <div>
                            <h3 class="text-sm font-semibold text-gray-900 dark:text-txt-body">
                                Zdieľaj svoje sebapoznanie
                                <span class="font-normal text-gray-400 dark:text-txt-body">(nepovinné)</span>
                            </h3>
                            <p class="mt-0.5 text-xs text-gray-500 dark:text-txt-body">
                                V testoch nie sú správne ani nesprávne odpovede — sú o tom, kto si a čo ťa baví.
                                Zdieľaním pomôžeš zamestnávateľovi lepšie pochopiť, či si navzájom sedíte.
                            </p>
                        </div>
                    </div>

                    <template v-if="myAssessments.length">
                        <p class="text-xs text-gray-500 dark:text-txt-body">
                            Vyber, ktoré výsledky firma uvidí. Uvidí presne to, čo je nižšie, bez bodov a skóre.
                        </p>

                        <SharedAssessmentPicker v-model="sharedAssessmentsSelection" :options="myAssessments" />
                    </template>

                    <p v-else class="text-sm text-gray-600 dark:text-txt-body">
                        Ešte nemáš žiadne výsledky. Testy trvajú pár minút a zamestnávateľ z nich uvidí, čo ťa baví
                        a v čom si silný/á. Vyplň ich v novom okne a keď sa sem vrátiš, budeš si ich môcť k tejto
                        prihláške pridať.
                    </p>

                    <NextTestForJob :done-types="myAssessmentTypes" :work-profile="job.workProfile" />

                    <CFormCheckbox v-model="includeLaterConsent">
                        Pridať k tejto prihláške aj testy, ktoré dokončím, kým je prihláška otvorená
                    </CFormCheckbox>
                    <p class="-mt-2 text-xs text-gray-500 dark:text-txt-body">
                        Pridajú sa len testy, z ktorých teraz výsledok nemáš. Čo firma vidí, môžeš kedykoľvek zmeniť
                        v Mojich aktivitách pri tejto prihláške.
                    </p>
                </div>

                <!-- Consents section -->
                <div class="rounded-xl border border-gray-200 dark:border-default p-4 space-y-3">
                    <CFormGroup v-if="isPaidService && jobMediationAgreement?.docUrl" :error="errors.jobMediationAgreementAccepted">
                        <CFormCheckbox v-model="jobMediationAgreementAccepted" required>
                            {{ $t(`${keyPrefix}.fields.paidService.agree`, lang) }}
                            <a class="link" :href="jobMediationAgreement.docUrl" target="_blank">{{
                                $t(`${keyPrefix}.fields.paidService.agreement`, lang)
                            }}</a>
                            <span v-html="$t(`${keyPrefix}.fields.paidService.caption`, lang)"></span>
                        </CFormCheckbox>
                    </CFormGroup>

                    <JobApplicationConsentCheckbox
                        v-if="jobApplicationConsent"
                        v-model="jobApplicationConsentAccepted"
                        :agreement="jobApplicationConsent"
                        :error="errors.jobApplicationConsentAccepted"
                        :lang="lang"
                        no-label
                        :org="org"
                        required
                    />

                    <MktConsentCheckbox
                        v-if="!userProfile && marketingConsent?.id"
                        v-model="marketingConsentAccepted"
                        :agreement="marketingConsent"
                        :lang="lang"
                        no-label
                    />
                </div>
            </div>
            <div v-else-if="step === 2">
                <p v-if="headline.description" class="text-gray-500 dark:text-txt-body">
                    {{ $t(headline.description, lang) }}
                </p>
                <div class="my-10 flex space-x-4">
                    <div class="w-1/2">
                        <LogInWithGoogle
                            full
                            :title="$t(`${keyPrefix}.buttons.google`, lang)"
                            @error="socialLoginErrorHandler"
                        />
                    </div>

                    <div class="w-1/2">
                        <LogInWithFacebook
                            full
                            :title="$t(`${keyPrefix}.buttons.facebook`, lang)"
                            @error="socialLoginErrorHandler"
                        />
                    </div>
                </div>

                <CLineBreak :title="$t(`${keyPrefix}.buttons.joiner`, lang)" />

                <div class="my-10">
                    <div class="w-full">
                        <CFormGroup :error="errors.email" :label="$t(`${keyPrefix}.fields.email`, lang)">
                            <CFormInput
                                v-model="credentials.email"
                                autocomplete="email"
                                class="mt-2 w-full"
                                data-e2e-id="signUpEmail"
                                name="email"
                            />
                        </CFormGroup>
                    </div>

                    <div class="mt-2 w-full">
                        <CFormGroup :error="errors.password" :label="$t(`${keyPrefix}.fields.password`, lang)">
                            <CFormInput
                                v-model="credentials.password"
                                autocomplete="password"
                                class="mt-2 w-full"
                                data-e2e-id="signUpPassword"
                                name="password"
                                type="password"
                            />
                        </CFormGroup>
                    </div>
                </div>

                <div class="grid grid-cols-2">
                    <CButton
                        class="text-left"
                        :disabled="state === 'loading'"
                        :loading="state === 'loading'"
                        @click="closeWithoutRegistration()"
                    >
                        {{ $t(`${keyPrefix}.buttons.notInterested`, lang) }}
                    </CButton>
                    <CButton
                        class="text-right"
                        :disabled="state === 'loading'"
                        :loading="state === 'loading'"
                        variant="primary"
                        @click="createProfile()"
                    >
                        {{ $t(`${keyPrefix}.buttons.createProfile`, lang) }}
                    </CButton>
                </div>
                <div class="my-10">
                    <p>
                        {{ $t(`${keyPrefix}.consents.intro`, lang) }}
                        <a class="link" href="/privacy/" target="_blank">
                            {{ $t(`${keyPrefix}.consents.privacy`, lang) }}
                        </a>

                        {{ $t(`${keyPrefix}.consents.joiner`, lang) }}

                        <a class="link" href="/terms/" target="_blank">
                            {{ $t(`${keyPrefix}.consents.terms`, lang) }} </a
                        >.
                    </p>
                </div>
            </div>
            <div v-else-if="step === 3">
                <FirstName
                    v-model="unregisteredUser.firstName"
                    :error="errors.firstName"
                    :label="$t(`${keyPrefix}.fields.firstName`, lang)"
                    required
                />

                <LastName
                    v-model="unregisteredUser.lastName"
                    :error="errors.lastName"
                    :label="$t(`${keyPrefix}.fields.lastName`, lang)"
                    required
                />

                <PlatformRulesCheckbox
                    v-model="hasAgreedToRules"
                    :error="errors.hasAgreedToRules"
                    :lang="lang"
                    required
                />

                <CFormGroup>
                    <CFormCheckbox v-model="under18">
                        {{ $t(`${keyPrefix}.consents.under18.label`, lang) }}
                        <CTooltip>
                            {{ $t(`${keyPrefix}.consents.under18.tooltip`, lang) }}
                        </CTooltip>
                    </CFormCheckbox>
                </CFormGroup>

                <CFormGroup
                    v-if="under18"
                    :error="errors.birthday"
                    :label="$t(`${keyPrefix}.fields.birthday.label`, lang)"
                    :label-info="$t(`${keyPrefix}.fields.birthday.info`, lang)"
                    required
                >
                    <BirthDayPicker v-model="userPrivateProfile.birthday" />
                </CFormGroup>

                <CButton
                    class="mt-8 w-full md:w-auto md:px-12"
                    :disabled="state === 'loading'"
                    :loading="state === 'loading'"
                    variant="primary"
                    @click="submitProfile()"
                >
                    {{ $t(`${keyPrefix}.buttons.finishRegistration`, lang) }}
                </CButton>
            </div>
            <div v-else-if="step === 4">
                <div class="text-center">
                    <p>
                        {{ $t(`${keyPrefix}.step4.text.part1`, lang) }}
                        <b>{{ credentials.email }}</b>
                        {{ $t(`${keyPrefix}.step4.text.part2`, lang) }}
                        <br /><br />
                        {{ $t(`${keyPrefix}.step4.text.part3`, lang) }}
                        <NuxtLink class="link" :to="{ name: 'auth-create-account' }">
                            {{ $t(`${keyPrefix}.step4.text.registerLink`, lang) }}
                        </NuxtLink>
                        {{ $t(`${keyPrefix}.step4.text.end`, lang) }}
                    </p>

                    <div class="w-full">
                        <CButton
                            :disabled="state === 'done' || state === 'loading'"
                            :loading="state === 'loading'"
                            @click="resendVerificationMail"
                        >
                            <template v-if="state === 'loading'">
                                {{ $t(`${keyPrefix}.buttons.sending`, lang) }}
                            </template>

                            <template v-else-if="state === 'done'">
                                {{ $t(`${keyPrefix}.buttons.sent`, lang) }}
                            </template>

                            <template v-else>
                                {{ $t(`${keyPrefix}.buttons.sendAgain`, lang) }}
                            </template>
                        </CButton>
                        <CButton @click="state = 'done'">
                            {{ $t(`${keyPrefix}.buttons.continue`, lang) }}
                        </CButton>
                    </div>
                </div>
            </div>
        </template>
        <template v-else>
            <template v-if="isPublicSectorWithRedirection">
                <p>
                    {{ $t(`${keyPrefix}.thanks.pubSecLink.title`, lang) }}
                </p>
                <p>
                    {{ $t(`${keyPrefix}.thanks.pubSecLink.top`, lang) }}
                    <a class="link" :href="job.externalLink" target="_blank">open.slovensko.sk</a>
                    <span v-html="$t(`${keyPrefix}.thanks.pubSecLink.bottom`, lang)"></span>
                </p>
            </template>
            <template v-else>
                <p
                    v-html="
                        $t(`${keyPrefix}.thanks.title`, lang, {
                            orgTitle: job.orgMeta.title
                        })
                    "
                ></p>
            </template>

            <FeedbackForm
                v-if="showFeedbackModal && !feedbackSubmitted"
                ref="feedbackForm"
                :action="feedbackAction"
                :lang="lang"
                :questions="feedbackQuestionsList"
                @submit="submitFeedback($event)"
            />

            <div class="mt-2 flex w-full">
                <CButton
                    v-if="!showFeedbackModal || feedbackSubmitted"
                    class="ml-auto w-full sm:w-auto"
                    size="large"
                    @click="close()"
                >
                    {{ $t(`${keyPrefix}.buttons.close`, lang) }}
                </CButton>
            </div>
        </template>

        </div>

        <!-- Sticky Footer Actions -->
        <div v-if="state !== 'done' && step === 1" class="shrink-0 border-t border-gray-100 dark:border-default bg-white dark:bg-surface-card px-5 sm:px-7 py-3 flex flex-col-reverse sm:flex-row gap-2 sm:justify-end">
            <CButton
                v-if="userProfile"
                class="w-full sm:w-auto"
                :disabled="!showSaveDraftButton || state === 'loading'"
                :loading="state === 'loading'"
                size="large"
                variant="link"
                @click.prevent="saveDraft()"
            >
                {{ $t(`${keyPrefix}.buttons.saveDraft`, lang) }}
            </CButton>
            <CButton
                class="w-full sm:w-auto sm:min-w-[180px]"
                :disabled="state === 'loading'"
                :loading="state === 'loading'"
                size="large"
                variant="primary"
                @click.prevent="submitApplication()"
            >
                {{
                    $t(
                        isPublicSectorWithRedirection
                            ? `${keyPrefix}.buttons.sign`
                            : `${keyPrefix}.buttons.send`,
                        lang
                    )
                }}
            </CButton>
        </div>
    </VueFinalModal>
    <VueFinalModal v-else v-model="showFeedbackModalThanks" class="flex justify-center items-center" content-class="flex flex-col max-w-md mx-4 p-6 bg-white dark:bg-surface-card border dark:border-default rounded-lg space-y-4" @update:model-value="handleFeedbackModalUpdate">
        <div class="text-center">
            <img alt="" src="@/assets/images/ico/feedbackThanks.svg" class="mx-auto mb-4" />
            <h2 class="text-xl font-semibold mb-4">{{ $t('feedbacks.modal.title.thanksForFeedback', 'sk') }}</h2>
            <p class="mb-6">Vďaka spätnej väzbe môžeme pre vás GROWNi zlepšovať.</p>
            <CButton
                class="w-1/2 uppercase"
                :disabled="state === 'loading'"
                :loading="state === 'loading'"
                size="large"
                variant="primary-150"
                @click="close()"
            >
                {{ $t('feedbacks.modal.buttons.ok', lang) }}
            </CButton>
        </div>
    </VueFinalModal>
</template>

<script setup lang="ts">
import { VueFinalModal } from 'vue-final-modal'
import dayjs from 'dayjs'
import { createUserWithEmailAndPassword, fetchSignInMethodsForEmail } from 'firebase/auth'
import { Timestamp } from 'firebase/firestore'
import { ValidationErrors } from 'last-validator'
import cloneDeep from 'lodash-es/cloneDeep'
import * as uuid from 'uuid'
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'

import { AgreementType } from '#shared/types/Agreement'
import type { Agreement } from '#shared/types/Agreement'
import { Feedback, FeedbackAction, FeedbackAnswerData, feedbackQuestions } from '#shared/types/Feedback'
import type { Job } from '#shared/types/Job'
import { JobPlaceType } from '#shared/types/Job'
import {
    ApplicantData,
    DefaultJobRequest,
    JobApplicationFormData,
    JobRequest,
    JobRequestFormData,
    SharedAssessment,
    UnregisteredUser
} from '#shared/types/JobRequest'
import { LoadingState } from '#shared/types/LoadingState'
import type { Org } from '#shared/types/Org'
import { Credentials, JobApplicationFormDraft, UserName, UserPrivateProfile, UserProfile } from '#shared/types/User'
import { getFullName, getLength, isEligibleForUsersFeedback, isJobForPublicSectorWithRedirection } from '#shared/utils'
import { useAgreement } from '@/composables/agreement/useAgreement'
import { useCheese } from '@/composables/useCheese'
import { useAuth } from '@/composables/auth/useAuth'
import { useEmailVerification } from '@/composables/auth/useEmailVerification'
import { useConsents } from '@/composables/consents/useConsents'
import { useExponea } from '@/composables/exponea/useExponea'
import { useFeedback } from '@/composables/feedback/useFeedback'
import { useHeadMeta } from '@/composables/head/useHeadMeta'
import { useJobs } from '@/composables/jobs/useJobs'
import { useSharedAssessments } from '@/composables/jobRequests/useSharedAssessments'
import CIcon from '@/components/ui/CIcon.vue'
import SavedJobCvInput from '@/components/jobs/SavedJobCvInput.vue'
import SharedAssessmentPicker from '@/components/jobs/SharedAssessmentPicker.vue'
import NextTestForJob from '@/components/jobs/fit/NextTestForJob.vue'
import { useOrgs } from '@/composables/orgs/useOrgs'
import { useUser } from '@/composables/user/useUser'
import { uploadFileToStorage } from '@/utils/uploadFileToStorage'
import { validateJobRequest } from '@/validators/jobRequests'
import { validateCredentials, validateProfileData } from '@/validators/userProfile'

// Firebase and Auth setup - use useAuth() for safe access
const { $firebase } = useNuxtApp()
const { track: trackCheese } = useCheese()
const { collections, createDocument } = $firebase || {}
const { getCurrentUser, getUserId } = useAuth()
const { sendVerificationEmail } = useEmailVerification()

// Get Firebase auth instance for registration operations (createUser, ...)
const firebaseAuth = $firebase?.auth
const { DEFAULT_CONSENT_VALIDITY_YEARS } = useConsents()

// Props
const props = defineProps<{
    job: Job
}>()

// Emits
const emit = defineEmits<{
    (e: 'close', jobRequest?: JobRequest): void
}>()

// Store and composables
const store = useApplicationStore()
const router = useRouter()
const { t: $t, t } = useI18n()
const { loadActualDocumentVersion } = useAgreement()
const { trackExponeaEvent } = useExponea()
const { saveFeedback } = useFeedback()
const { headMetaTrackGTM } = useHeadMeta()
const { isJobTypePaidService, formatJobSalary } = useJobs()
const { loadOrg } = useOrgs()
const { giveConsent } = useConsents()
const { completeRegistration, updateUserPrivateProfile } = useUser()
const { buildSharedAssessments } = useSharedAssessments()

// Constants
const keyPrefix = 'jobView.sendJobModal'
// Keep in sync with the jobs/{jobId} match in storage.rules
const MAX_FILE_SIZE_MB = 10
const PDF_MIME_TYPE = 'application/pdf'
const DOCX_MIME_TYPE = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'

// Refs
const step = ref(1)
const org = ref<Org>({} as Org)
const userProfile = ref<UserProfile | undefined>(undefined)
const feedbackForm = ref<any>(null)
const clFileInput = ref<HTMLInputElement | null>(null)
const jobRequest = ref<JobRequestFormData>(cloneDeep(DefaultJobRequest))
const unregisteredUser = ref<UnregisteredUser>({} as UnregisteredUser)
const state = ref<LoadingState>('idle')
const errors = ref<ValidationErrors>({})
const cvFile = ref<File | null>(null)
const clFiles = ref<any[]>([])
const credentials = ref<Credentials>({} as Credentials)
const userPrivateProfile = ref<UserPrivateProfile>({} as UserPrivateProfile)
const hasAgreedToRules = ref(false)
const under18 = ref(false)
const feedbackSubmitted = ref(false)
const jobMediationAgreement = ref<Agreement | undefined>(undefined)
const jobMediationAgreementAccepted = ref(false)
const jobApplicationConsent = ref<Agreement | undefined>(undefined)
const jobApplicationConsentAccepted = ref(false)
const marketingConsent = ref<Agreement | undefined>(undefined)
const marketingConsentAccepted = ref(false)
const showFeedbackModalThanks = ref(false)
// Results the candidate may share, and the ones they picked
const myAssessments = ref<SharedAssessment[]>([])
const sharedAssessmentsSelection = ref<SharedAssessment[]>([])
const includeLaterConsent = ref(false)
const myAssessmentTypes = computed(() => myAssessments.value.map((assessment) => assessment.type))
const jobFormDrafts = ref<JobApplicationFormDraft[]>([])
const trackGtmEvent = ref(false)
const showModal = ref(true)
const completedJobRequest = ref<JobRequest | undefined>(undefined)

// Computed properties
const feedbackAction = computed(() => FeedbackAction.JOB_APPLICATION)
const feedbackQuestionsList = computed(() => feedbackQuestions[FeedbackAction.JOB_APPLICATION])

const isPaidService = computed((): boolean => {
    return isJobTypePaidService(org.value, props.job)
})

const showSaveDraftButton = computed((): boolean => {
    // if all inputs are empty === saveDraftButton IS disabled
    if (
        !(jobRequest.value.phone || '').toString().trim() &&
        !(jobRequest.value.email || '').toString().trim() &&
        !(jobRequest.value.about || '').toString().trim() &&
        !(jobRequest.value.motivation || '').toString().trim() &&
        !(jobRequest.value.linkedInUrl || '').toString().trim()
    )
        return false

    const draft = getDraft()

    // if draft does not exist === button IS NOT disabled
    if (!draft) return true

    const isEmail = !(jobRequest.value.email === draft.data.email)
    const isPhone = !(jobRequest.value.phone === draft.data.phone)
    const isAbout = !(jobRequest.value.about === draft.data.about)
    const isMotivation = !(jobRequest.value.motivation === draft.data.motivation)
    const isLinkedInUrl = !(jobRequest.value.linkedInUrl === draft.data.linkedInUrl)

    // If some value differs from the one in draft === button IS NOT disabled
    return isAbout || isMotivation || isLinkedInUrl || isEmail || isPhone
})

const isPublicSectorWithRedirection = computed((): boolean => {
    return isJobForPublicSectorWithRedirection(props.job, org.value!)
})

const headline = computed((): { title: string; description?: string } => {
    return state.value === 'done'
        ? { title: `${keyPrefix}.done.title` }
        : {
              title: `${keyPrefix}.step${step.value}.title`,
              description: step.value === 2 ? `${keyPrefix}.step2.description` : undefined
          }
})

const showFeedbackModal = computed((): boolean => {
    return Boolean(!userProfile.value || isEligibleForUsersFeedback(FeedbackAction.JOB_APPLICATION, userProfile.value!))
})

const lang = computed((): string => {
    return props.job.lang || 'sk'
})

// Job summary header texts
const jobPlaceText = computed((): string => {
    if (!props.job?.jobPlace?.length) return ''
    const places = props.job.jobPlace.map((p) => t(`jobView.jobPlace.type.${p}`, lang.value).toString())
    const jobLocation = props.job.jobLocation?.join(', ')
    const placeWithLocation = props.job.jobPlace?.includes(JobPlaceType.office)
        ? JobPlaceType.office
        : JobPlaceType.hybrid
    if (jobLocation && props.job.jobPlace?.includes(placeWithLocation)) {
        return `${places.join(', ')} (${jobLocation})`
    }
    return places.join(', ')
})

const jobTimeText = computed((): string => {
    if (!props.job?.jobTime?.length) return ''
    return props.job.jobTime.map((time) => t(`jobView.jobTime.type.${time}`, lang.value).toString()).join(', ')
})

const jobTypeText = computed((): string => {
    if (!props.job?.type?.length) return ''
    return props.job.type.map((type) => t(`jobForm.fields.type.values.${type}`, lang.value).toString()).join(' / ')
})

const salaryText = computed((): string => {
    if (!props.job) return ''
    return formatJobSalary(
        props.job.type || [],
        props.job.salaryMin || 0,
        props.job.salaryMax || 0,
        t('jobView.jobSalary.internship', lang.value).toString(),
        t('jobView.jobSalary.compensationNotSpecified', lang.value).toString(),
        t('jobView.jobSalary.prefixFrom', lang.value).toString(),
        t('jobView.jobSalary.prefixTo', lang.value).toString(),
        t(`jobView.jobSalary.unit.${props.job.salaryUnit}`, lang.value).toString()
    )
})

// Methods
const getDraft = (): JobApplicationFormDraft | undefined => {
    if (!jobFormDrafts.value) return undefined
    else {
        const draft = jobFormDrafts.value.find(
            (draft) => draft.jobId === props.job.id && draft.orgId === props.job.orgId
        )
        return draft
    }
}

const generateFileName = (docType: string, name: string) => {
    return `${docType}_GROWNi_${name.replace(' ', '_')}_${uuid.v4()}`
}

const formatFileSize = (value: number): string => {
    return `${(value / 1024 / 1024).toFixed(1)} MB`
}

const openFilePicker = () => {
    if (clFileInput.value) {
        clFileInput.value.click()
    }
}

const fileError = (key: string, file: File): string => {
    return t(`${keyPrefix}.fields.${key}`, { name: file.name, max: MAX_FILE_SIZE_MB }, { locale: lang.value })
}

const isFileTooLarge = (file: File): boolean => {
    return file.size > MAX_FILE_SIZE_MB * 1024 * 1024
}

const setFileError = (field: 'cvFile' | 'clFile', message?: string) => {
    const { [field]: _previous, ...otherErrors } = errors.value
    errors.value = message ? { ...otherErrors, [field]: message } : otherErrors
}

// Some systems report no MIME type for .docx, so fall back to the extension
const getAttachmentMimeType = (file: File): string | undefined => {
    if (file.type === PDF_MIME_TYPE || file.type === DOCX_MIME_TYPE) return file.type
    if (!file.type && /\.docx$/i.test(file.name)) return DOCX_MIME_TYPE
    if (!file.type && /\.pdf$/i.test(file.name)) return PDF_MIME_TYPE
    return undefined
}

const handleFileChange = (e: any) => {
    const file: File | undefined = e.target.files[0]
    if (clFileInput.value) {
        clFileInput.value.value = ''
    }
    if (!file) return

    const mimeType = getAttachmentMimeType(file)
    if (!mimeType) {
        setFileError('clFile', fileError('clFile.unsupported', file))
        return
    }
    if (isFileTooLarge(file)) {
        setFileError('clFile', fileError('fileTooLarge', file))
        return
    }

    setFileError('clFile')
    clFiles.value = [
        ...clFiles.value,
        {
            // The storage path extension and content type both come from file.type
            file: file.type === mimeType ? file : new File([file], file.name, { type: mimeType }),
            ownName: file.name.replace(/\.[^.]+$/, '')
        }
    ]
}

const clearFile = (index: number) => {
    clFiles.value.splice(index, 1)
}

// Upload files
const uploadFile = (file: any, name: string): Promise<string> => {
    return uploadFileToStorage(file, `jobs/${props.job.id}`, name)
}

const uploadApplicantFiles = async (applicantData: ApplicantData) => {
    const applicantName = getFullName(userProfile.value || applicantData.applicant!)
    if (cvFile.value) {
        jobRequest.value.cvUrl = await uploadFile(cvFile.value!, generateFileName('CV', applicantName))
    }
    if (clFiles.value.length) {
        jobRequest.value.clUrl = []
        for (const clFile of clFiles.value) {
            // Unique suffix: two applicants often pick the same file name (ActivityCardJobContact hides it)
            const fileUrl = await uploadFile(clFile.file, `${clFile.ownName}_${uuid.v4()}`)
            jobRequest.value.clUrl.push(fileUrl)
        }
    }
}

// Validation methods
const validateJobRequestForm = async (jobApplication: JobApplicationFormData) => {
    const { errors: validationErrors, isValid } = await validateJobRequest(
        jobApplication,
        isPaidService.value && Boolean(jobMediationAgreement.value?.docUrl),
        getUserId()
    )

    errors.value = validationErrors

    return isValid
}

const validateCredentialsForm = async () => {
    const { errors: validationErrors, isValid } = await validateCredentials(credentials.value)

    errors.value = validationErrors

    return isValid
}

const validateProfileData2 = async () => {
    const { errors: validationErrors, isValid } = await validateProfileData({
        userPrivateProfile: userPrivateProfile.value!,
        userProfile: userProfile.value!,
        under18: under18.value,
        consents: { hasAgreedToRules: hasAgreedToRules.value }
    })

    errors.value = validationErrors

    return isValid
}

const isEmailInUse = async (email: string) => {
    if (!firebaseAuth) return 0
    return (await fetchSignInMethodsForEmail(firebaseAuth, email)).length
}

const checkUnregisteredEmailNotUsed = async () => {
    if (!userProfile.value && (await isEmailInUse(jobRequest.value.email))) {
        throw new Error('Tento email už je registrovaný na GROWNi. Môže ho používať iba prihlásený používateľ.')
    }
}

// Create, submit & save methods
const createJobRequest = async (jobId: string, applicantData: ApplicantData) => {
    await uploadApplicantFiles(applicantData)

    // Only registered candidates share self-assessment results: the ones they picked, and tests
    // completed later only when they agreed to it.
    const assessmentData =
        userProfile.value && (sharedAssessmentsSelection.value.length || includeLaterConsent.value)
            ? {
                  sharedAssessmentsConsent: sharedAssessmentsSelection.value.length > 0,
                  sharedAssessments: sharedAssessmentsSelection.value,
                  sharedAssessmentsIncludeLater: includeLaterConsent.value,
                  sharedAssessmentsOfferedTypes: myAssessmentTypes.value,
                  sharedAssessmentsUpdatedAt: Timestamp.now()
              }
            : {}

    const jobRequest2: JobRequest = await createDocument(
        collections.jobRequests,
        {
            ...jobRequest.value,
            ...applicantData,
            ...assessmentData,
            jobId,
            orgId: props.job.orgId
        },
        { withTimestamps: true }
    )

    return jobRequest2
}

const submitApplication = async () => {
    try {
        state.value = 'loading'

        if (
            !(await validateJobRequestForm({
                ...jobRequest.value,
                ...unregisteredUser.value,
                jobMediationAgreementAccepted: jobMediationAgreementAccepted.value,
                // Skip consent validation if agreement not loaded
                jobApplicationConsentAccepted: jobApplicationConsent.value?.id ? jobApplicationConsentAccepted.value : true,
                cvFile: cvFile.value
            } as JobApplicationFormData))
        ) {
            state.value = 'error'
            return
        }

        await checkUnregisteredEmailNotUsed()

        let unregisteredUserData: { email?: string } & UserName = {}
        if (!userProfile.value) {
            unregisteredUserData = {
                email: jobRequest.value.email,
                firstName: unregisteredUser.value.firstName!,
                lastName: unregisteredUser.value.lastName!
            }

            if (marketingConsentAccepted.value) {
                // Track Double Opt-In because user is not registered on platform
                trackExponeaEvent({
                    eventName: 'double_opt_in',
                    userId: jobRequest.value.email,
                    eventProperties: {
                        action: 'new',
                        email: jobRequest.value.email,
                        phone: jobRequest.value.phone,
                        firstName: unregisteredUser.value.firstName,
                        lastName: unregisteredUser.value.lastName,
                        source: 'Job application of unregistered user',
                        consent_list: [
                            {
                                action: 'accept',
                                category: 'marketing',
                                valid_until: dayjs(new Date()).add(DEFAULT_CONSENT_VALIDITY_YEARS, 'year').unix()
                            }
                        ]
                    }
                })

                // Give marketing consent for unregistered user
                if (marketingConsent.value?.id) {
                    const marketingConsentRecord = await giveConsent(marketingConsent.value, {
                        user: {
                            email: jobRequest.value.email,
                            firstName: unregisteredUser.value.firstName!,
                            lastName: unregisteredUser.value.lastName!
                        }
                    })

                    jobRequest.value.consents = {
                        ...jobRequest.value.consents,
                        [marketingConsent.value.type]: {
                            accepted: true,
                            agreementId: marketingConsent.value.id,
                            consentId: marketingConsentRecord.id
                        }
                    }
                }
            } else {
                // When marketing consent is not accepted update only user properties
                trackExponeaEvent({
                    eventName: 'update_user_properties',
                    userId: jobRequest.value.email,
                    eventProperties: {
                        email: jobRequest.value.email,
                        phone: jobRequest.value.phone,
                        firstName: unregisteredUser.value.firstName,
                        lastName: unregisteredUser.value.lastName,
                        source: 'Job application of unregistered user'
                    }
                })
            }
        }

        const createdJobRequest = await createJobRequest(props.job.id!, {
            ...(userProfile.value ? { applicantId: userProfile.value.id } : { applicant: unregisteredUserData })
        } as ApplicantData)
        
        completedJobRequest.value = createdJobRequest
        trackCheese('job_application_submitted', {
            job_id: props.job.id!,
            application_id: createdJobRequest.id!,
            applicant_type: userProfile.value ? 'registered' : 'guest'
        })

        trackGtmEvent.value = true

        const agreements: Agreement[] = []
        if (jobApplicationConsent.value?.id) {
            agreements.push(jobApplicationConsent.value)
        }
        if (jobMediationAgreementAccepted.value && jobMediationAgreement.value?.id) {
            agreements.push(jobMediationAgreement.value)
        }

        // Save consents for job request
        for (const agreement of agreements) {
            await giveConsent(agreement, {
                jobId: props.job!.id,
                jobRequestId: createdJobRequest.id,
                orgId: org.value!.id,
                user: userProfile.value ? { id: userProfile.value.id } : unregisteredUserData
            })
        }

        if (userProfile.value) {
            state.value = 'done'
        } else {
            credentials.value!.email = jobRequest.value.email
            step.value++
            state.value = 'idle'
        }

        if (isPublicSectorWithRedirection.value && props.job.externalLink) {
            window.open(props.job.externalLink, '_blank')
        }

        if ((userPrivateProfile.value?.jobFormDrafts && userProfile.value) || router.currentRoute.value.query.toggleModal) {
            removeDraft({ jobId: props.job.id } as JobApplicationFormDraft)
            await updateUserDrafts(userProfile.value!.id)
        }

        router.push({
            name: 'praca-ako-dalej',
            params: { jobId: props.job.id, jobLang: props.job.lang || 'sk' }
        })

        close()
    } catch (e) {
        console.error('Submit application error:', e)
        state.value = 'error'
        const errorMessage = e instanceof Error && e.message ? e.message : 'An error occurred while submitting the application.'
        errors.value = { general: errorMessage }
    }
}

const processRegistration = async () => {
    if (!firebaseAuth) {
        throw new Error('Firebase auth not available')
    }
    try {
        await createUserWithEmailAndPassword(firebaseAuth, credentials.value.email, credentials.value.password!)
    } catch (e) {
        console.error((e as Error).message)
    }
}

const createProfile = async () => {
    if (!(await validateCredentialsForm())) {
        return
    }

    try {
        state.value = 'loading'
        await processRegistration()
        step.value++
        state.value = 'idle'
    } catch (e) {
        errors.value = { general: e }
        state.value = 'error'
    }
}

const submitProfile = async () => {
    state.value = 'loading'

    userProfile.value = {
        ...store.userProfile!,
        firstName: unregisteredUser.value.firstName,
        lastName: unregisteredUser.value.lastName
    }
    userPrivateProfile.value = {
        ...store.userPrivateProfile!,
        phone: jobRequest.value.phone
    }
    if (!(await validateProfileData2())) {
        state.value = 'error'
        return
    }

    try {
        await completeRegistration({
            userProfile: userProfile.value,
            userPrivateProfile: userPrivateProfile.value
        })

        await store.reloadUserProfile()
        const currentUser = getCurrentUser()
        if (currentUser) {
            await sendVerificationEmail(currentUser)
        }
        step.value++
        state.value = 'idle'
    } catch (e) {
        errors.value.general = e
        console.error((e as Error).message)
        state.value = 'error'
    }
}

const resendVerificationMail = async () => {
    try {
        state.value = 'loading'
        const currentUser = getCurrentUser()
        if (currentUser) {
            await sendVerificationEmail(currentUser)
        }
        state.value = 'idle'
    } catch (e) {
        state.value = 'error'
    }
}

const submitFeedback = async (answers: FeedbackAnswerData[]) => {
    await saveFeedback({
        answers,
        ...(userProfile.value ? { userId: userProfile.value.id } : { email: jobRequest.value.email }),
        action: FeedbackAction.JOB_APPLICATION
    } as Partial<Feedback>)

    await store.reloadUserProfile()
    feedbackSubmitted.value = true
    showFeedbackModalThanks.value = true
}

// Draft methods
const removeDraft = (incomingDraft: JobApplicationFormDraft) => {
    jobFormDrafts.value = jobFormDrafts.value.filter((draft) => draft.jobId !== incomingDraft.jobId)
}

const createDraft = (draft: JobApplicationFormDraft | undefined) => {
    if (!draft) {
        const newDraft: JobApplicationFormDraft = {
            jobId: props.job.id,
            orgId: props.job.orgId,
            createdAt: Timestamp.fromDate(new Date()),
            data: {
                phone: jobRequest.value.phone,
                email: jobRequest.value.email,
                about: jobRequest.value.about,
                motivation: jobRequest.value.motivation,
                linkedInUrl: jobRequest.value.linkedInUrl
            }
        }
        return newDraft
    } else {
        const updDraft: JobApplicationFormDraft = {
            ...draft,
            updatedAt: new Date(),
            data: {
                phone: jobRequest.value.phone === draft.data.phone ? draft.data.phone : jobRequest.value.phone,
                email: jobRequest.value.email === draft.data.email ? draft.data.email : jobRequest.value.email,
                about: jobRequest.value.about === draft.data.about ? draft.data.about : jobRequest.value.about,
                motivation:
                    jobRequest.value.motivation === draft.data.motivation
                        ? draft.data.motivation
                        : jobRequest.value.motivation,
                linkedInUrl:
                    jobRequest.value.linkedInUrl === draft.data.linkedInUrl
                        ? draft.data.linkedInUrl
                        : jobRequest.value.linkedInUrl
            }
        }
        removeDraft(draft)
        return updDraft
    }
}

const { showSuccess } = useToast()

const showSuccessModal = async (modalTitle: string) => {
    showSuccess(modalTitle)
}

const updateUserDrafts = async (uid: string) => {
    state.value = 'loading'
    const modalTitle = t(`${keyPrefix}.draft.sent`, lang.value) as string
    try {
        await updateUserPrivateProfile(uid, { jobFormDrafts: jobFormDrafts.value }).then(async () => {
            await store.reloadUserProfile()
            await showSuccessModal(modalTitle)
            state.value = 'idle'
        })
    } catch (e) {
        console.error(e)
    }
    close()
}

const saveDraft = async () => {
    const draft = getDraft()
    const draftUpdated = createDraft(draft)

    try {
        jobFormDrafts.value.push(draftUpdated)
        await updateUserDrafts(userProfile.value!.id)
    } catch (e) {
        console.error(e)
    }
}

const closeWithoutRegistration = () => {
    state.value = 'done'
}

const handleModalUpdate = (value: boolean) => {
    if (!value) {
        close()
    }
}

const handleFeedbackModalUpdate = (value: boolean) => {
    if (!value) {
        close()
    }
}

const close = () => {
    showModal.value = false
    emit('close', completedJobRequest.value)
}

// Other methods
const reloadProfile = async () => {
    return store.reloadUserProfile()
}

// The results the candidate may share. Loaded again when they come back from taking a test in
// another tab, so a result finished meanwhile can be added. The first load preselects the tests
// they already share in their TalentBase profile; later loads keep the current selection.
const loadMyAssessments = async (preselectFromTalentBase = false) => {
    const userId = userProfile.value?.id
    if (!userId) return

    try {
        const assessments = await buildSharedAssessments(userId)
        const selectedTypes = new Set(
            (preselectFromTalentBase
                ? userProfile.value?.talentBaseUserProfile?.sharedAssessments ?? []
                : sharedAssessmentsSelection.value
            ).map((assessment) => assessment.type)
        )

        myAssessments.value = assessments
        sharedAssessmentsSelection.value = assessments.filter((assessment) => selectedTypes.has(assessment.type))
    } catch (e) {
        console.warn('Could not load self-assessment results:', e)
    }
}

const onWindowFocus = () => {
    loadMyAssessments()
}

// Watchers
watch(cvFile, (file: any) => {
    if (!file) return

    // Some mobile file providers omit MIME types for PDFs.
    if (!file.type && /\.pdf$/i.test(file.name)) {
        cvFile.value = new File([file], file.name, { type: PDF_MIME_TYPE })
        return
    }

    // Tell the applicant why the file disappeared instead of clearing it silently
    if (file.type !== PDF_MIME_TYPE) {
        setFileError('cvFile', fileError('cvFile.pdfOnly', file))
        cvFile.value = null
        return
    }
    if (isFileTooLarge(file)) {
        setFileError('cvFile', fileError('fileTooLarge', file))
        cvFile.value = null
        return
    }

    setFileError('cvFile')
})

// Lifecycle hooks
onMounted(() => window.addEventListener('focus', onWindowFocus))
onBeforeUnmount(() => window.removeEventListener('focus', onWindowFocus))

onMounted(async () => {
    try {
        state.value = 'loading'
        await reloadProfile()
        org.value = await loadOrg(props.job.orgId)
        
        // Load agreements by type with error handling
        const agreementPromises = [
            loadActualDocumentVersion(AgreementType.jobMediationAgreement).catch(e => {
                console.warn('Job mediation agreement not found:', e)
                return null
            }),
            loadActualDocumentVersion(AgreementType.jobApplicationConsent).catch(e => {
                console.warn('Job application consent not found:', e)
                return null
            }),
            loadActualDocumentVersion(AgreementType.marketingConsent).catch(e => {
                console.warn('Marketing consent not found:', e)
                return null
            })
        ]
        
        const [jobMediationAgreementData, jobApplicationConsentData, marketingConsentData] = await Promise.all(agreementPromises)
        
        jobMediationAgreement.value = jobMediationAgreementData || undefined
        jobApplicationConsent.value = jobApplicationConsentData || undefined
        marketingConsent.value = marketingConsentData || undefined

    const userPrivateProfileData = store.userPrivateProfile

    if (userPrivateProfileData?.jobFormDrafts && userPrivateProfileData?.id) {
        jobFormDrafts.value = cloneDeep(userPrivateProfileData.jobFormDrafts)

        const draft = getDraft()
        if (draft) {
            jobRequest.value = {
                ...jobRequest.value,
                about: draft.data.about || '',
                motivation: draft.data.motivation || '',
                linkedInUrl: draft.data.linkedInUrl || '',
                email: String(draft.data.email ?? userPrivateProfileData.contactEmail ?? ''),
                phone: String(draft.data.phone ?? userPrivateProfileData.phone ?? '')
            }
        }
    }

    if (!(jobRequest.value.email || '').toString().trim() && userPrivateProfileData?.contactEmail) {
        jobRequest.value.email = String(userPrivateProfileData.contactEmail)
    }

    if (!(jobRequest.value.phone || '').toString().trim() && userPrivateProfileData?.phone) {
        jobRequest.value.phone = String(userPrivateProfileData.phone)
    }

    userProfile.value = store.userProfile

    // Fire-and-forget so it never blocks the modal from becoming interactive.
    loadMyAssessments(true)

    jobMediationAgreementAccepted.value = Boolean(
        jobRequest.value.consents?.[AgreementType.jobMediationAgreement]?.accepted
    )

    jobApplicationConsentAccepted.value = Boolean(
        jobRequest.value.consents?.[AgreementType.jobApplicationConsent]?.accepted
    )

        marketingConsentAccepted.value = Boolean(
            unregisteredUser.value.consents?.[AgreementType.marketingConsent]?.accepted
        )
        state.value = 'idle'
    } catch (error) {
        console.error('Error initializing modal:', error)
        state.value = 'error'
        errors.value = { general: 'Failed to load modal data. Please try again.' }
    }
})

// Expose methods for head meta
defineExpose({
    head: computed(() => {
        return trackGtmEvent.value
            ? headMetaTrackGTM(`event: 'user_request', request_type: 'job', modul_subtype: '${props.job.type}'`)
            : {}
    })
})
</script>
